import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  const [permissoesEfetivas, setPermissoesEfetivas] = useState(null);

  const api = useMemo(() => axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }), [token]);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions(null);
    setPermissoesEfetivas(null);
  }, []);

  // Update axios headers when token changes
  useEffect(() => {
    const interceptor = api.interceptors.response.use(response => response, error => {
      if (token && error.response?.status === 401) clearSession();
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(interceptor);
  }, [token, api, clearSession]);

  const fetchPermissions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/auth/me/permissoes');
      setPermissions(response.data.effective_permissions);
      setPermissoesEfetivas(response.data.permissoes_efetivas || []);
    } catch (error) {
      if (error.response?.status === 401) clearSession();
    }
  }, [token, api, clearSession]);

  useEffect(() => {
    let active = true;
    if (!token) {
      setLoading(false);
      return;
    }
    const fetchUser = async () => {
      try {
        const [userResponse, permissionsResponse] = await Promise.all([
          api.get('/auth/me'), api.get('/auth/me/permissoes')
        ]);
        if (active) {
          setUser(userResponse.data);
          setPermissions(permissionsResponse.data.effective_permissions);
          setPermissoesEfetivas(permissionsResponse.data.permissoes_efetivas || []);
        }
      } catch (error) {
        if (active && error.response?.status === 401) clearSession();
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchUser();
    return () => { active = false; };
  }, [token, api, clearSession]);

  const login = async (email, senha) => {
    const response = await api.post('/auth/login', { email, senha });
    const { token: newToken, usuario } = response.data;
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(usuario);
    return usuario;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  };

  const logout = async () => {
    try {
      if (token) await api.post('/auth/logout');
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Saída local concluída, mas não foi possível confirmar a revogação no servidor.');
      }
    } finally {
      clearSession();
    }
  };

  // Permission check helpers
  const canViewServiceRevenue = permissions?.ver_valor_recebido_servicos || false;
  const canViewServicePayout = permissions?.ver_valor_pago_servicos || false;
  const canViewStockCosts = permissions?.ver_custos_estoque || false;
  const canUseAI = permissions?.usar_agente_ia || false;
  const isAdmin = permissions?.is_admin || false;
  const isGestor = permissions?.is_gestor || false;
  const canEditPatient = permissions?.can_edit_patient || false;
  const canViewFullCPF = permissions?.can_view_full_cpf || false;
  const isMaster = (user?.perfil || '').toLowerCase() === 'master_admin';
  // RBAC: any-of check on effective permissions (master_admin always true)
  const can = (...keys) => isMaster || (!!permissoesEfetivas && keys.some((k) => permissoesEfetivas.includes(k)));

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    api,
    isAuthenticated: !!user,
    permissions,
    canViewServiceRevenue,
    canViewServicePayout,
    canViewStockCosts,
    canUseAI,
    isAdmin,
    isGestor,
    canEditPatient,
    canViewFullCPF,
    isMaster,
    can,
    permissoesEfetivas,
    refetchPermissions: fetchPermissions
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
